import { logoUrl } from '../../api/branding.api.js';
import { HADIR_NAME } from '@attendance/shared';
import { useBrandingStore } from '../../stores/brandingStore.js';

/**
 * THE HADIR MARK — what a company with no logo of its own shows.
 *
 * It drew the first letter of the app name on the brand colour, and the app
 * name defaulted to "Attendance", so every fresh install introduced itself
 * with an "A" (owner's settings review, 2026-09-24). The real mark already
 * shipped as the favicon — the violet tile and the wordmark as outlines, no
 * font dependency — so it is the default now, and "Reset to Hadir logo" in
 * Settings puts it back after an upload. `BASE_URL` so it also resolves when
 * the console is served from a subfolder (`/demo/`).
 */
const HADIR_MARK = `${import.meta.env.BASE_URL}favicon.svg`;

interface Props {
  size?: 'sm' | 'md' | 'lg';
  fallback?: 'monogram' | 'text' | 'none';
}

const SIZE_CLASS = {
  sm: 'h-7',
  md: 'h-10',
  lg: 'h-16',
};

export function BrandLogo({ size = 'md', fallback = 'monogram' }: Props): JSX.Element {
  const branding = useBrandingStore((s) => s.branding);
  const version = useBrandingStore((s) => s.version);
  const appName = branding?.appName ?? HADIR_NAME;

  if (branding?.logoUrl) {
    return (
      <img
        src={logoUrl(version || undefined)}
        alt={appName}
        className={`${SIZE_CLASS[size]} w-auto object-contain`}
      />
    );
  }

  if (fallback === 'text') {
    /**
     * The wordmark is `--primary`, NOT the tenant's `--brand-primary`.
     *
     * This is text on the page, so it is measured against `--background`, and
     * `--background` inverts between themes while a tenant hex is one value for
     * both. index.css can express that split for its own seed — it overrides
     * `--brand-primary` to #c9beff in dark precisely because the light violet
     * read 3.52:1 on the dark page — but the store writes the tenant colour as
     * an INLINE property on `<html>`, which outranks both `:root` and `.dark`,
     * so once a tenant saves a colour that override can never apply again.
     *
     * There is no way to derive a page-legible tone from a single hex without
     * knowing the theme, and the fill-luminance trick used by the monogram
     * below does not transfer: that one owns its background, this one does not.
     * So until per-tenant M3 re-seeding lands (index.css, "Per-tenant M3
     * re-seeding is a follow-up") the wordmark uses the theme-aware token that
     * tracks the seed brand: 6.54:1 light, 10.85:1 dark.
     */
    // The wordmark is Archivo 700 with the display steps' own tight tracking —
    // `Hadir Dashboard.dc.html` sets the rail's wordmark at 16px/700/−0.2px, and
    // the display steps are where that pairing lives. `text-2xl`/`text-base`/
    // `text-sm` were stock Tailwind sizes, i.e. Instrument Sans at default
    // tracking: the product's own name set in the UI-text face.
    return (
      <span
        className={`font-bold text-primary ${
          size === 'lg' ? 'text-h2' : size === 'md' ? 'text-h3' : 'text-copy font-display'
        }`}
      >
        {appName}
      </span>
    );
  }

  if (fallback === 'monogram') {
    // The rail's tile size from the handoff (34px, `Hadir Dashboard.dc.html:34`)
    // and its two neighbours; the mark carries its own rounded tile.
    const dim =
      size === 'lg'
        ? 'h-12 w-12'
        : size === 'md'
          ? // 34px, the handoff's rail tile. `h-9` is 36 — two over, and
            // visible against a 34px wordmark row.
            'h-[2.125rem] w-[2.125rem]'
          : 'h-7 w-7';
    return <img src={HADIR_MARK} alt={appName} className={`shrink-0 ${dim}`} />;
  }

  return <span />;
}
